import { Route } from '@mews-distributor/common';
import { LoggerService } from '@mews/framework';
import { WallClockDate, WallClockDateService } from '@mews/framework/datetime';
import { fetchAvailabilityBlockWithCheck } from '../api/actions/availabilityBlocks';
import {
    getAvailabilityBlockIdFromQuery,
    getCurrentRoute,
    getEndDateFromQuery,
    getSelectedCityId,
    getStartDateFromQuery,
} from '../modules/app/selectors';
import { getConfigurationDaysInterval, getStartDateOffset } from '../modules/configuration/selectors';
import { isServiceHourly } from '../modules/entities/services/selectors';
import { getEndDate, getSelectedHotelId, getStartDate } from '../modules/resourceOrder/selectors';
import { fetchInitialCityServiceAvailabilities } from '../modules/serviceAvailability';
import { getOccupiedStartDates } from '../modules/serviceAvailability/selectors';

import { getValidAvailabilityBlockInterval, isValidAvailabilityBlock } from '../selectors/views/availabilityBlocks';
import {
    getMinimalStartDate,
    getValidStartDateEndDate,
    lastFetchedDateInServiceAvailability,
} from '../selectors/views/dates';
import {
    getSelectedCityEnterprisesIds,
    getSelectedCityEnterpriseTimezone,
    hasSelectedCityDayUseBookableServices,
} from '../selectors/views/selectedCityEnterprises';
import { DistributorPromiseThunkAction, DistributorThunkAction } from '../types';
import { getRestrictedMaxDate } from '../utils/dates';
import { setDates } from './dates';

const HOURLY_SERVICE_DAY_INTERVAL = 0;

interface SetInitialDatesOptions {
    minimalStartDate: WallClockDate | null;
    maximalEndDate: WallClockDate | null;
    shouldSetNonDisabledDates: boolean;
}
export const setInitialDates =
    ({
        minimalStartDate,
        maximalEndDate,
        shouldSetNonDisabledDates = true,
    }: SetInitialDatesOptions): DistributorThunkAction =>
    (dispatch, getState, { appClockService, loggerService, calendarService }) => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);

        const isHourlyService = enterpriseId != null && isServiceHourly(state, { enterpriseId });
        const hasDayUse = hasSelectedCityDayUseBookableServices(state, {});
        const minimalDayDifference = isHourlyService || hasDayUse ? 0 : 1;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const configurationStartDateOffset = getStartDateOffset(state) || 0;
        const startDateOffset = Math.max(0, configurationStartDateOffset);
        const configurationDaysInterval = getConfigurationDaysInterval(state);
        const nowUtc = appClockService.getAppNowUtc();
        const nowDate = getMinimalStartDate(state, { nowUtc });
        const minStartDate = minimalStartDate || nowDate;

        const minStartDateWithOffset = WallClockDateService.addDays(minStartDate, startDateOffset);

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const preselectedStartDateString = getStartDateFromQuery(state) || getStartDate(state);
        const startDate =
            (preselectedStartDateString != null &&
                tryParseMaxDate(preselectedStartDateString, minStartDate, { loggerService })) ||
            minStartDateWithOffset;

        // end date can not be before start date
        const minimalEndDate = WallClockDateService.max(
            startDate,
            WallClockDateService.addDays(startDate, minimalDayDifference)
        );
        const minimalEndDateWithDefaultInterval = WallClockDateService.max(
            minimalEndDate,
            WallClockDateService.addDays(startDate, configurationDaysInterval)
        );
        const isWithoutAvailabilityBlock = getAvailabilityBlockIdFromQuery(state) == null;
        const resourceOrderEndDate = isWithoutAvailabilityBlock ? getEndDate(state) : null;
        const defaultEndDate = isHourlyService ? startDate.value : resourceOrderEndDate;
        // availability blocks end date fix for standalone Booking Engine
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const preSelectedEndDateString = getEndDateFromQuery(state) || defaultEndDate;

        const preferredEndDate =
            (preSelectedEndDateString != null &&
                tryParseMaxDate(preSelectedEndDateString, minimalEndDate, { loggerService })) ||
            minimalEndDateWithDefaultInterval;

        const restrictedMaximalEndDate = getRestrictedMaxDate(maximalEndDate, isHourlyService || hasDayUse);
        const endDate = restrictedMaximalEndDate
            ? WallClockDateService.min(restrictedMaximalEndDate, preferredEndDate)
            : preferredEndDate;

        if (shouldSetNonDisabledDates) {
            const queryEndDateString = getEndDateFromQuery(state);
            const timezone = getSelectedCityEnterpriseTimezone(state, {});
            const enterpriseIds = getSelectedCityEnterprisesIds(state, {});
            const occupiedStartDates = getOccupiedStartDates(state, { enterpriseIds });
            const lastDateInServiceAvailabilityData = lastFetchedDateInServiceAvailability(state, {});
            const maximalEndDateConstraint = restrictedMaximalEndDate ?? preferredEndDate;
            const queryDaysInterval = WallClockDateService.diffDays(preferredEndDate, startDate);
            const configOrQueryInterval = queryDaysInterval < 0 ? configurationDaysInterval : queryDaysInterval;
            const daysInterval = isHourlyService ? HOURLY_SERVICE_DAY_INTERVAL : configOrQueryInterval;

            let validStartDateEndDate = getValidStartDateEndDate({
                calendarService,
                isDayService: hasDayUse,
                startDate,
                maximalEndDate: maximalEndDateConstraint,
                occupiedStartDates,
                timezone,
                daysInterval,
                isHourlyService,
                lastDateInServiceAvailabilityData,
            });

            if (validStartDateEndDate == null) {
                // to optimize the performance, we call this only when query endDate is present
                if (queryEndDateString != null) {
                    validStartDateEndDate = getValidStartDateEndDate({
                        calendarService,
                        timezone,
                        occupiedStartDates,
                        startDate: minStartDate,
                        daysInterval: configurationDaysInterval,
                        maximalEndDate: maximalEndDateConstraint,
                        isDayService: hasDayUse,
                        isHourlyService,
                    });
                }
                if (validStartDateEndDate == null) {
                    loggerService.log('fully-booked', 'The selected city is fully booked.', {
                        isWithoutAvailabilityBlock,
                    });
                    if (isWithoutAvailabilityBlock) {
                        dispatch(setDates(startDate.value, endDate.value));
                        return;
                    }
                    dispatch(setDates(null, null));
                    return;
                }
            }

            const { nonDisabledStartDate, nonDisabledEndDate } = validStartDateEndDate;

            dispatch(setDates(nonDisabledStartDate.value, nonDisabledEndDate.value));
            return;
        }

        dispatch(setDates(startDate.value, endDate.value));
    };

const tryParseMaxDate = (
    dateString: string,
    minimalDate: WallClockDate,
    { loggerService }: { loggerService: LoggerService }
) => {
    let date;
    try {
        const parsedDate = WallClockDateService.parse(dateString);
        date = WallClockDateService.max(parsedDate, minimalDate);
    } catch (e) {
        loggerService.log('max-date-parse-error', 'Failed to parse the MaxDate from input.', {
            dateString,
            minimalDate,
        });
    }

    return date;
};

const restrictCalendarByAvailability =
    (): DistributorThunkAction =>
    (dispatch, getState, { appClockService }) => {
        const state = getState();
        const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
        const nowUtc = appClockService.getAppNowUtc();

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!availabilityBlockId || !isValidAvailabilityBlock(state, { availabilityBlockId, nowUtc })) {
            dispatch(
                setInitialDates({ shouldSetNonDisabledDates: true, minimalStartDate: null, maximalEndDate: null })
            );
            return;
        }

        const { availabilityBlockStartDate, availabilityBlockEndDate } = getValidAvailabilityBlockInterval(state, {
            availabilityBlockId,
            nowUtc,
        });

        const minimalStartDate = getMinimalStartDate(state, { nowUtc });
        const minimalAvailabilityBlockRestrictedStartDate = availabilityBlockStartDate
            ? WallClockDateService.max(availabilityBlockStartDate, minimalStartDate)
            : minimalStartDate;

        dispatch(
            setInitialDates({
                minimalStartDate: minimalAvailabilityBlockRestrictedStartDate,
                maximalEndDate: availabilityBlockEndDate ?? null,
                shouldSetNonDisabledDates: true,
            })
        );
    };

export const initializeAvailabilityData = (): DistributorPromiseThunkAction => async (dispatch, getState) => {
    await dispatch(fetchAvailabilityBlockWithCheck());
    const state = getState();
    const cityId = getSelectedCityId(state);

    if (cityId != null) {
        void dispatch(fetchInitialCityServiceAvailabilities({ cityId })).then(() => {
            const currentRoute = getCurrentRoute(getState());
            if (currentRoute === Route.Dates || currentRoute == null) {
                dispatch(restrictCalendarByAvailability());
            }
        });
    }

    dispatch(restrictCalendarByAvailability());
};
